import type Keycloak from 'keycloak-js'

export const DEFAULT_REFRESH_INTERVAL_MS = 30_000
export const MIN_REFRESH_INTERVAL_MS = 1_000
export const MAX_TOKEN_REFRESH_FAILURES = 3

/**
 * Le variabili Vite sono stringhe e possono mancare (la build Docker esclude `.env`):
 * un valore assente, non numerico o <= 0 non deve mai diventare un setInterval a 0 ms.
 */
export function resolveRefreshInterval(raw: unknown): number {
  const value = Number(raw)
  if (raw === undefined || raw === null || raw === '' || !Number.isFinite(value) || value <= 0) {
    return DEFAULT_REFRESH_INTERVAL_MS
  }
  return Math.max(value, MIN_REFRESH_INTERVAL_MS)
}

interface TokenRefreshOptions {
  intervalMs: number
  maxFailures?: number
  /** Chiamata a ogni ciclo fallito (che non esaurisce i tentativi). */
  onFailure: (attempt: number, max: number) => void
  /** Chiamata quando i cicli falliti consecutivi raggiungono il massimo. */
  onExhausted: () => void
}

/**
 * Rinnova periodicamente il token. Una sola `updateToken` è in volo alla volta e il contatore
 * dei fallimenti conta i cicli, non le promise rifiutate: un fallimento transitorio viene
 * ritentato al ciclo successivo e solo `maxFailures` cicli consecutivi falliti portano al logout.
 */
export function startTokenRefresh(keycloak: Keycloak, options: TokenRefreshOptions): () => void {
  const max = options.maxFailures ?? MAX_TOKEN_REFRESH_FAILURES
  let failures = 0
  let inFlight = false
  let stopped = false

  const timer = setInterval(() => {
    if (inFlight || stopped) return
    inFlight = true
    keycloak
      .updateToken(5)
      .then(() => {
        failures = 0
      })
      .catch(() => {
        failures++
        if (failures >= max) {
          stopped = true
          clearInterval(timer)
          options.onExhausted()
        } else {
          options.onFailure(failures, max)
        }
      })
      .finally(() => {
        inFlight = false
      })
  }, options.intervalMs)

  return () => {
    stopped = true
    clearInterval(timer)
  }
}

/**
 * Rinnovo condiviso per i 401: le richieste concorrenti attendono la stessa promise
 * e, se il rinnovo fallisce, il logout parte una volta sola.
 * Ritorna true se il token è stato rinnovato.
 */
export function createSharedRefresh(keycloak: Keycloak, onFailure: () => void) {
  let pending: Promise<boolean> | null = null

  return (): Promise<boolean> => {
    if (!pending) {
      pending = keycloak
        .updateToken(-1)
        .then(() => true)
        .catch(() => {
          try {
            onFailure()
          } catch (err) {
            console.error('Token refresh failure handler threw:', err)
          }
          return false
        })
        .finally(() => {
          pending = null
        })
    }
    return pending
  }
}
